import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { 
  BookOpen, Clock, Users, Play, CheckCircle, Lock,
  ChevronRight, ChevronLeft, GraduationCap, Code, Brain, 
  Cloud, Database, Sparkles, Layers, FileText, Video,
  HelpCircle, Award, ArrowLeft
} from 'lucide-react';

const lessonTypeIcons = {
  video: Video,
  text: FileText,
  quiz: HelpCircle
};

const categoryIcons = {
  full_stack_development: Code,
  ai_machine_learning: Brain,
  generative_ai: Sparkles,
  cloud_devops: Cloud,
  data_science: Database,
  cybersecurity: Lock,
  mobile_development: Layers,
  other: BookOpen
};

const difficultyColors = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700'
};

const FinanceTrainingCourseDetail = () => {
  const { courseId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCourse = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/finance-training/courses/${courseId}`);
      setCourse(response.data.course);
      setLessons(response.data.lessons);
      setEnrollment(response.data.enrollment);
    } catch (error) {
      console.error('Failed to fetch course:', error);
    } finally {
      setLoading(false);
    }
  }, [api, courseId]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  const handleEnroll = async () => {
    try {
      await api.post(`/finance-training/enroll/${courseId}`);
      fetchCourse();
    } catch (error) {
      console.error('Failed to enroll:', error);
    }
  };

  const handleStartLesson = (lessonId) => {
    navigate(`/finance-training/lesson/${lessonId}`);
  };

  const handleGenerateCertificate = async () => {
    try {
      const response = await api.post(`/finance-training/courses/${courseId}/certificate`);
      if (response.data.certificate) {
        navigate(`/finance-training/certificate/${response.data.certificate.id}`);
      }
    } catch (error) {
      console.error('Failed to generate certificate:', error);
    }
  };

  const getNextLesson = () => {
    if (!enrollment) return lessons[0];
    
    // Find first incomplete lesson
    const incompleteLesson = lessons.find(lesson => !lesson.is_completed);
    return incompleteLesson || lessons[0];
  };

  const formatCategoryName = (category) => {
    return category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-48" />
          <div className="h-64 bg-muted rounded" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 h-96 bg-muted rounded" />
            <div className="h-96 bg-muted rounded" />
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!course) {
    return (
      <MainLayout>
        <Card className="p-12 text-center">
          <BookOpen className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
          <h2 className="text-2xl font-bold mt-4">Course Not Found</h2>
          <p className="text-muted-foreground mt-2">
            The course you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button className="mt-4" onClick={() => navigate('/finance-training')}>
            Browse Courses
          </Button>
        </Card>
      </MainLayout>
    );
  }

  const CategoryIcon = categoryIcons[course.category] || BookOpen;
  const completedLessons = enrollment?.completed_lessons?.length || 0;
  const progressPercentage = enrollment?.progress_percentage || 0;
  const isCompleted = enrollment?.status === 'completed';

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="course-detail">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/finance-training')} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Library
        </Button>

        {/* Hero Section */}
        <div className="relative rounded-xl overflow-hidden">
          <div 
            className="h-64 bg-cover bg-center"
            style={{ 
              backgroundImage: course.thumbnail_url 
                ? `url(${course.thumbnail_url})` 
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            <div className="absolute inset-0 bg-black/60" />
            <div className="absolute inset-0 p-6 flex flex-col justify-end">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="bg-white/20 text-white border-white/30">
                  <CategoryIcon className="h-3 w-3 mr-1" />
                  {formatCategoryName(course.category)}
                </Badge>
                <Badge className={difficultyColors[course.difficulty]}>
                  {course.difficulty}
                </Badge>
                {course.is_featured && (
                  <Badge className="bg-amber-500">Featured</Badge>
                )}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{course.title}</h1>
              <p className="text-white/80 mt-2 max-w-2xl line-clamp-2">{course.description}</p>
              
              <div className="flex items-center gap-6 mt-4 text-white/90">
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {course.estimated_hours} hours
                </span>
                <span className="flex items-center gap-1">
                  <BookOpen className="h-4 w-4" />
                  {lessons.length} lessons
                </span>
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {course.enrolled_count} enrolled
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Syllabus */}
          <div className="lg:col-span-2 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Course Syllabus
                </CardTitle>
                <CardDescription>
                  {lessons.length} lessons • {course.estimated_hours} hours total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-2">
                    {lessons.map((lesson, index) => {
                      const LessonIcon = lessonTypeIcons[lesson.type] || FileText;
                      const isLocked = !enrollment && index > 0;
                      const isComplete = lesson.is_completed;
                      
                      return (
                        <div
                          key={lesson.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                            isLocked 
                              ? 'bg-muted/50 cursor-not-allowed' 
                              : 'hover:bg-muted cursor-pointer'
                          } ${isComplete ? 'border-green-200 bg-green-50' : ''}`}
                          onClick={() => !isLocked && enrollment && handleStartLesson(lesson.id)}
                          data-testid={`lesson-${lesson.id}`}
                        >
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                            isComplete 
                              ? 'bg-green-100 text-green-600' 
                              : isLocked 
                                ? 'bg-muted text-muted-foreground' 
                                : 'bg-primary/10 text-primary'
                          }`}>
                            {isComplete ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : isLocked ? (
                              <Lock className="h-5 w-5" />
                            ) : (
                              <LessonIcon className="h-5 w-5" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                              <h4 className="font-medium truncate">{lesson.title}</h4>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                              <Badge variant="outline" className="text-xs">
                                {lesson.type}
                              </Badge>
                              <span>{lesson.duration_minutes} min</span>
                            </div>
                          </div>
                          
                          {enrollment && !isLocked && (
                            <ChevronRight className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Course Description */}
            <Card>
              <CardHeader>
                <CardTitle>About This Course</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">
                  {course.description}
                </p>
                
                {course.tags && course.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {course.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Enrollment Card */}
            <Card className="sticky top-4">
              <CardHeader>
                {enrollment ? (
                  <>
                    <CardTitle className="text-lg">Your Progress</CardTitle>
                    <CardDescription>
                      {completedLessons} of {lessons.length} lessons completed
                    </CardDescription>
                  </>
                ) : (
                  <>
                    <CardTitle className="text-lg">Start Learning</CardTitle>
                    <CardDescription>
                      Enroll to access all course content
                    </CardDescription>
                  </>
                )}
              </CardHeader>
              
              <CardContent>
                {enrollment && (
                  <div className="space-y-4 mb-4">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span>Progress</span>
                        <span className="font-semibold">{Math.round(progressPercentage)}%</span>
                      </div>
                      <Progress value={progressPercentage} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-primary">{completedLessons}</div>
                        <div className="text-muted-foreground">Completed</div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold">{lessons.length - completedLessons}</div>
                        <div className="text-muted-foreground">Remaining</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
              
              <CardFooter className="flex-col gap-2">
                {enrollment ? (
                  isCompleted ? (
                    <>
                      <Button className="w-full" onClick={handleGenerateCertificate}>
                        <Award className="h-4 w-4 mr-2" />
                        {enrollment.certificate_id ? 'View Certificate' : 'Get Certificate'}
                      </Button>
                      <Button variant="outline" className="w-full" onClick={() => handleStartLesson(lessons[0]?.id)}>
                        Review Course
                      </Button>
                    </>
                  ) : (
                    <Button className="w-full" onClick={() => handleStartLesson(getNextLesson()?.id)}>
                      <Play className="h-4 w-4 mr-2" />
                      {progressPercentage > 0 ? 'Continue Learning' : 'Start Course'}
                    </Button>
                  )
                ) : (
                  <>
                    <Button className="w-full" onClick={handleEnroll}>
                      <GraduationCap className="h-4 w-4 mr-2" />
                      Enroll Now — Free
                    </Button>
                    <p className="text-xs text-muted-foreground text-center">
                      Get full access to all lessons and quizzes
                    </p>
                  </>
                )}
              </CardFooter>
            </Card>

            {/* Course Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Difficulty</span>
                  <Badge className={difficultyColors[course.difficulty]}>
                    {course.difficulty}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">{course.estimated_hours} hours</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Lessons</span>
                  <span className="font-medium">{lessons.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Enrolled</span>
                  <span className="font-medium">{course.enrolled_count} students</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FinanceTrainingCourseDetail;
